import { AfterContentInit, Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { merge, Observable, of, Subject } from 'rxjs';
import { catchError, finalize, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { BoxType } from 'src/app/shared/components/box/box.component';
import { Imovel } from 'src/app/shared/models/imovel';
import { ClientService } from 'src/app/shared/services/client.service';
import { SessionManager } from 'src/app/shared/util/session-manager';
import { PAGE_TITLE } from 'src/globals';

@Component({
  selector: 'app-compare',
  templateUrl: './compare.component.html',
  styleUrls: ['./compare.component.scss'],
})
export class CompareComponent implements OnInit, AfterContentInit {
  codigo: string;
  BoxType = BoxType;
  reloadData$ = new Subject<any>();
  properties$: Observable<Imovel[]>;
  tags$: Observable<string[]>;
  removing: boolean = false;
  adding: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private titleService: Title,
    private clientService: ClientService,
    private sm: SessionManager
  ) {
    this.titleService.setTitle(`Comparação - ${PAGE_TITLE}`);
  }

  ngOnInit(): void {
    //
    if (!this.sm.hasToken()) {
      this.router.navigate(['/login'], { queryParams: { redirectUrl: this.router.url } });
      return;
    }

    // param para adicionar
    this.route.params.subscribe(params => {
      const { code } = params;
      if (!code) return;
      this.clientService.addToCompare(code).subscribe(
        () => {
          this.router.navigateByUrl('/imoveis/comparar');
        },
        error => {
          SessionManager.handleHttpError(error, 'Falha ao adicionnar o imóvel para comparação');
          // this.router.navigate(['/imoveis/comparar'], { replaceUrl: true });
          this.codigo = code;
        }
      );
    });

    this.prepareData();
  }

  ngAfterContentInit(): void {
    this.enableDragToScroll();
  }

  enableDragToScroll() {
    var container = document.getElementById('i-container-comparable');
    let isDown = false;
    let startX;
    let scrollLeft;

    container.addEventListener('mousedown', e => {
      isDown = true;
      container.classList.add('i-dragging');
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    });
    container.addEventListener('mouseleave', () => {
      isDown = false;
      container.classList.remove('i-dragging');
    });
    container.addEventListener('mouseup', () => {
      isDown = false;
      container.classList.remove('i-dragging');
    });
    container.addEventListener('mousemove', e => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 1.5; // acelerar
      container.scrollLeft = scrollLeft - walk;
      // console.debug(walk);
    });
  }

  prepareData() {
    this.properties$ = merge(this.reloadData$, of(null)).pipe(
      switchMap(() => this.clientService.getCompareList()),
      startWith(Array(2).fill(new Imovel())),
      tap(result => this.titleService.setTitle(`Comparação (${result?.length}) - ${PAGE_TITLE}`)),
      catchError(error => {
        SessionManager.handleHttpError(error, 'Falha ao consultar imóveis para comparação');
        return of([] as Imovel[]);
      }),
      shareReplay(1)
    );
    this.tags$ = this.properties$.pipe(
      map(result =>
        [...result.map(x => x.caracteristicas.map(y => y.nome)), ...result.map(x => x.infraestruturas.map(y => y.nome))]
          .flat()
          .sort()
          .filter((v, i, a) => a.indexOf(v) === i)
      )
    );
  }

  onRemove(codigo: string) {
    this.removing = true;
    this.clientService
      .delFromCompareList(codigo)
      .pipe(finalize(() => (this.removing = false)))
      .subscribe(
        () => {
          this.reloadData$.next();
          this.enableDragToScroll();
        },
        error => {
          SessionManager.handleHttpError(error, 'Falha ao remover o imóvel da comparação');
        }
      );
  }

  onAdd() {
    this.adding = true;
    this.clientService
      .addToCompare(this.codigo)
      .pipe(finalize(() => (this.adding = false)))
      .subscribe(
        () => {
          this.codigo = '';
          this.reloadData$.next();
        },
        error => {
          SessionManager.handleHttpError(error, 'Falha ao adicionar o imóvel para comparação');
        }
      );
  }
}
